import React, { useState, useMemo, useRef } from 'react'
import { Plus } from 'lucide-react'

const VariableReference = ({ 
  value, 
  onChange, 
  onBlur,
  availableVariables = [], 
  placeholder = "Enter text or use variables...",
  title,
  className = "",
  multiline = false,
  rows = 2
}) => {
  // Ensure value is never null or undefined
  const safeValue = value === null || value === undefined ? "" : String(value)
  
  const [showVariableMenu, setShowVariableMenu] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [cursorPosition, setCursorPosition] = useState(0)
  const inputRef = useRef(null)

  // Deduplicate variables by name and filter by search term
  const filteredVariables = useMemo(() => {
    // Create a Map to deduplicate by variable name
    const uniqueVariables = new Map()
    availableVariables.forEach(variable => {
      if (!uniqueVariables.has(variable.name)) {
        uniqueVariables.set(variable.name, variable)
      }
    })
    
    // Convert back to array and filter by search term
    return Array.from(uniqueVariables.values())
      .filter(variable => 
        variable.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [availableVariables, searchTerm])

  const insertVariable = (variableName) => {
    const variableRef = `{{${variableName}}}`
    const newValue = safeValue.slice(0, cursorPosition) + variableRef + safeValue.slice(cursorPosition)
    onChange(newValue)
    setShowVariableMenu(false)
    setSearchTerm('') // Clear search when inserting
    
    // Set cursor position after the inserted variable
    setTimeout(() => {
      if (inputRef.current) {
        const newCursorPosition = cursorPosition + variableRef.length
        inputRef.current.setSelectionRange(newCursorPosition, newCursorPosition)
        inputRef.current.focus()
      }
    }, 0)
  }

  const handleInputChange = (e) => {
    onChange(e.target.value)
  }

  const handleKeyDown = (e) => {
    if (e.key === '{' && e.ctrlKey) {
      e.preventDefault()
      setShowVariableMenu(!showVariableMenu)
    }
  }

  const handleInputSelect = (e) => {
    setCursorPosition(e.target.selectionStart || 0)
  }

  const handleInputClick = (e) => {
    setCursorPosition(e.target.selectionStart || 0)
  }

  const handleVariableButtonClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    
    // Store current cursor position before opening menu
    if (inputRef.current) {
      setCursorPosition(inputRef.current.selectionStart || 0)
    }
    setShowVariableMenu(!showVariableMenu)
  }

  const handleInputBlur = (e) => {
    // Ensure the value is updated when input loses focus
    onChange(e.target.value)
    
    // Call the onBlur callback if provided
    if (onBlur) {
      onBlur(e)
    }
  }

  return (
    <div className={`variable-reference-container ${className}`}>
      <div className="input-with-variable-button">
        {multiline ? (
          <textarea
            ref={inputRef}
            value={safeValue}
            title={title ?? safeValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onSelect={handleInputSelect}
            onClick={handleInputClick}
            onBlur={handleInputBlur}
            placeholder={placeholder}
            className="variable-textarea"
            rows={rows}
          />
        ) : (
          <input
            ref={inputRef}
            type="text"
            value={safeValue}
            title={title ?? safeValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onSelect={handleInputSelect}
            onClick={handleInputClick}
            onBlur={handleInputBlur}
            placeholder={placeholder}
            className={`variable-input ${className}`}
          />
        )}
        {availableVariables.length > 0 && (
          <button
            type="button"
            className="variable-button"
            onClick={handleVariableButtonClick}
            title="Insert variable (Ctrl+{)"
          >
            <Plus size={14} />
          </button>
        )}
      </div>
      
      {showVariableMenu && availableVariables.length > 0 && (
        <div 
          className="variable-menu dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* <div className="variable-menu-header">
            <span>Available Variables</span>
            <button 
              className="close-menu-button"
              onClick={() => {
                setShowVariableMenu(false)
                setSearchTerm('')
              }}
            >
              ×
            </button>
          </div> */}
          
          {/* Search input */}
          <div className="variable-search dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none">
            <input
              type="text"
              placeholder="Search variables..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input dark:bg-[#1f2937] dark:text-white dark:focus:outline-none"
              autoFocus
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            />
          </div>
          
          <div className="variable-list dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none">
            {filteredVariables.length > 0 ? (
              filteredVariables.map((variable) => (
                <button
                  key={variable.name}
                  className="variable-option dark:bg-[#1f2937] dark:text-white dark:focus:outline-none dark:hover:bg-card"
                  onClick={(e) => {
                    e.stopPropagation()
                    insertVariable(variable.name)
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  title={variable.name}
                >
                  <span className="variable-name dark:text-white">{variable.name}</span>
                </button>
              ))
            ) : (
              <div className="no-variables-found dark:bg-[#1f2937] dark:text-white dark:border-none dark:focus:outline-none">
                <span>No variables found</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default VariableReference 